import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Edit2, X, ArrowLeft, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { Movie, Screening, SeatSplitting } from '../types';
import BookingLayoutInterface from './BookingLayoutInterface';
import BookingLayoutDesigner from './BookingLayoutDesigner';

interface ManageScreeningsProps {
  onClose: () => void;
}

export default function ManageScreenings({ onClose }: ManageScreeningsProps) {
  const [movies, setMovies] = useState<Movie[]>([]);
  const [screenings, setScreenings] = useState<Screening[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Screening | null>(null);
  const [split, setSplit] = useState<SeatSplitting | null>(null);
  const [formData, setFormData] = useState({
    start_time: '',
    location: '',
    available_seats: 100,
    silver_percentage: 60,
    gold_percentage: 30,
    diamond_percentage: 10,
  });
  const [showDesigner, setShowDesigner] = useState(false);
  const [layoutDraft, setLayoutDraft] = useState<{ name: string; rows: number; cols: number; seats: boolean[][] } | null>(null);
  const editingRef = useRef<HTMLDivElement | null>(null);
  const [bookedSeatIds, setBookedSeatIds] = useState<string[]>([]);

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);
      const [moviesRes, screeningsRes] = await Promise.all([
        supabase.from('movies').select('*').order('created_at', { ascending: false }),
        supabase.from('screenings').select('*').order('start_time', { ascending: true }),
      ]);
      if (moviesRes.error || screeningsRes.error) {
        toast.error('Failed to load screenings');
      } else {
        setMovies(moviesRes.data || []);
        setScreenings(screeningsRes.data || []);
      }
      setLoading(false);
    }
    fetchAll();
  }, []);

  const movieTitle = (movie_id: number) => {
    const m = movies.find(x => x.id === movie_id);
    return m ? m.title : `Movie #${movie_id}`;
  };

  const startEdit = async (screening: Screening) => {
    setEditing(screening);
    const { data: splitRows, error } = await supabase
      .from('seatssplitting')
      .select('*')
      .eq('screening_id', screening.id)
      .limit(1);
    if (error || !splitRows || splitRows.length === 0) {
      toast.error('Seat allocation data not found');
      setSplit(null);
    } else {
      const s = splitRows[0] as SeatSplitting;
      setSplit(s);
      const total = screening.available_seats;
      const silver_percentage = Math.round((s.silver_total / total) * 100);
      const gold_percentage = Math.round((s.gold_total / total) * 100);
      const diamond_percentage = 100 - silver_percentage - gold_percentage;
      setFormData({
        start_time: new Date(screening.start_time).toISOString().slice(0, 16),
        location: screening.location,
        available_seats: screening.available_seats,
        silver_percentage,
        gold_percentage,
        diamond_percentage,
      });
    }
    const { data: layoutRows } = await supabase
      .from('seat_layouts')
      .select('*')
      .eq('screening_id', screening.id)
      .limit(1);
    if (layoutRows && layoutRows.length > 0) {
      const layout = layoutRows[0];
      setLayoutDraft({ name: layout.layout_name, rows: layout.rows, cols: layout.cols, seats: layout.seats });
      const { data: bookedRows } = await supabase
        .from('booking_seats')
        .select('seat_id')
        .eq('screening_id', screening.id);
      setBookedSeatIds((bookedRows || []).map((r: any) => r.seat_id));
    } else {
      setLayoutDraft(null);
      setBookedSeatIds([]);
    }
  };

  useEffect(() => {
    if (editingRef.current && editing) {
      editingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editing]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
    }));
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!formData.start_time || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (formData.silver_percentage + formData.gold_percentage + formData.diamond_percentage !== 100) {
      toast.error('Seat percentages must total 100%');
      return;
    }

    const newSilver = Math.round(formData.available_seats * (formData.silver_percentage / 100));
    const newGold = Math.round(formData.available_seats * (formData.gold_percentage / 100));
    const newDiamond = formData.available_seats - newSilver - newGold;

    const { data: updatedScreening, error: screeningErr } = await supabase
      .from('screenings')
      .update({
        start_time: new Date(formData.start_time).toISOString(),
        location: formData.location,
        available_seats: formData.available_seats,
      })
      .eq('id', editing.id)
      .select();

    if (screeningErr) {
      toast.error('Failed to update screening');
      return;
    }

    const current = split;
    const silver_booked = Math.min(current?.silver_booked || 0, newSilver);
    const gold_booked = Math.min(current?.gold_booked || 0, newGold);
    const diamond_booked = Math.min(current?.diamond_booked || 0, newDiamond);

    const { error: splitErr } = await supabase
      .from('seatssplitting')
      .update({
        silver_total: newSilver,
        silver_booked,
        gold_total: newGold,
        gold_booked,
        diamond_total: newDiamond,
        diamond_booked,
      })
      .eq('screening_id', editing.id);

    if (splitErr) {
      toast.error('Failed to update seat allocation');
      return;
    }

    toast.success('Screening updated');
    const updated = updatedScreening?.[0] as Screening;
    setScreenings(prev => prev.map(s => (s.id === updated.id ? updated : s)));
    setEditing(null);
    setSplit(null);
  };

  const deleteScreening = async (screening: Screening) => {
    const first = window.confirm('You selected to delete this screening. Click OK to continue.');
    if (!first) return;
    const second = window.confirm('This will permanently delete the screening and related data. Click OK to confirm.');
    if (!second) return;
    const { error } = await supabase
      .from('screenings')
      .delete()
      .eq('id', screening.id);
    if (error) {
      toast.error('Failed to delete screening');
      return;
    }
    toast.success('Screening deleted');
    setScreenings(prev => prev.filter(s => s.id !== screening.id));
    if (editing && editing.id === screening.id) {
      setEditing(null);
      setSplit(null);
    }
  };

  return createPortal(
    <>
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg shadow-2xl w-full max-w-[95vw] lg:max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h2 className="text-2xl font-bold text-white">Edit Screenings</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <p className="text-gray-300">Loading...</p>
          ) : (
            <div className="space-y-3">
              {screenings.map(s => (
                <div key={s.id} className="bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold">{movieTitle(s.movie_id)}</p>
                    <p className="text-sm text-gray-300">{new Date(s.start_time).toLocaleString()} • {s.location}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => startEdit(s)} className="text-indigo-300 hover:text-indigo-200">
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button onClick={() => deleteScreening(s)} className="text-red-300 hover:text-red-200">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {editing && (
          <div ref={editingRef} className="border-t border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Editing: {movieTitle(editing.movie_id)}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Start Time *</label>
                <input
                  type="datetime-local"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Location *</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Total Available Seats</label>
                <input
                  type="number"
                  name="available_seats"
                  value={formData.available_seats}
                  onChange={handleChange}
                  min={1}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                />
              </div>
              {layoutDraft ? (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-300 mb-3">Seat Layout Preview</h3>
                  <BookingLayoutInterface
                    layout={{ rows: layoutDraft.rows, cols: layoutDraft.cols, seats: layoutDraft.seats }}
                    layoutName={layoutDraft.name}
                    bookedSeats={bookedSeatIds}
                    seatNumberingStart={editing?.seat_numbering_start || 'top-left'}
                  />
                </div>
              ) : (
                <div className="bg-gray-700/50 rounded-lg p-4 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-300">Seat Allocation</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Silver Seats: {formData.silver_percentage}%</label>
                    <input
                      type="range"
                      name="silver_percentage"
                      value={formData.silver_percentage}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      className="w-full h-2 bg-gray-600 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Gold Seats: {formData.gold_percentage}%</label>
                    <input
                      type="range"
                      name="gold_percentage"
                      value={formData.gold_percentage}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      className="w-full h-2 bg-gray-600 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Diamond Seats: {formData.diamond_percentage}%</label>
                    <input
                      type="range"
                      name="diamond_percentage"
                      value={formData.diamond_percentage}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      className="w-full h-2 bg-gray-600 rounded-lg"
                    />
                  </div>
                  {formData.silver_percentage + formData.gold_percentage + formData.diamond_percentage !== 100 && (
                    <p className="text-sm text-red-400">Total must be 100%</p>
                  )}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setEditing(null); setSplit(null); }}
                  className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveEdit}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                >
                  Save Changes
                </button>
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setShowDesigner(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  Edit Seat Layout
                </button>
                {layoutDraft && (
                  <p className="mt-2 text-xs text-gray-400">Layout: {layoutDraft.name} ({layoutDraft.rows}x{layoutDraft.cols})</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    <BookingLayoutDesigner
      show={showDesigner}
      initialLayout={layoutDraft ? { rows: layoutDraft.rows, cols: layoutDraft.cols, seats: layoutDraft.seats } : null}
      initialName={layoutDraft?.name || ''}
      initialSeatNumberingStart={editing?.seat_numbering_start || 'top-left'}
      onClose={() => setShowDesigner(false)}
      onSeatNumberingStartChange={async (value) => {
        if (!editing) return;
        setEditing({ ...editing, seat_numbering_start: value });
        const { data: updatedScr, error: updateErr } = await supabase
          .from('screenings')
          .update({ seat_numbering_start: value })
          .eq('id', editing.id)
          .select();
        if (updateErr) {
          toast.error('Failed to update seat numbering start');
        } else if (updatedScr && updatedScr[0]) {
          const updated = updatedScr[0] as Screening;
          setScreenings(prev => prev.map(s => (s.id === updated.id ? updated : s)));
        }
      }}
      onSave={async (name, layout, seatNumberingStart) => {
        if (!editing) return;
        const { error: layoutErr } = await supabase
          .from('seat_layouts')
          .upsert({
            screening_id: editing.id,
            layout_name: name,
            rows: layout.rows,
            cols: layout.cols,
            seats: layout.seats,
          }, { onConflict: 'screening_id' });
        if (layoutErr) {
          toast.error('Failed to save layout');
        } else {
          toast.success('Seat layout saved');
          setLayoutDraft({ name, rows: layout.rows, cols: layout.cols, seats: layout.seats });
          const { data: updatedScr, error: updateErr } = await supabase
            .from('screenings')
            .update({ seat_numbering_start: seatNumberingStart })
            .eq('id', editing.id)
            .select();
          if (updateErr) {
            toast.error('Failed to save seat numbering start');
          } else if (updatedScr && updatedScr[0]) {
            const updated = updatedScr[0] as Screening;
            setScreenings(prev => prev.map(s => (s.id === updated.id ? updated : s)));
          }
        }
      }}
    />
    </>,
    document.body
  );
}
